/**
 * GENERATED from source/degrees-of-freedom.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const DEGREES_OF_FREEDOM = {
  "key": "degrees-of-freedom",
  "title": "Degrees of Freedom",
  "category": "Concepts",
  "platform": {
    "key": "degrees-of-freedom",
    "sub": "Freedom Is Only Useful If You Can Organize It.",
    "duration": "8–10 min read",
    "chapter": {
      "id": "degrees-of-freedom",
      "title": "Degrees of Freedom"
    }
  },
  "cert": {
    "key": "degrees-of-freedom-cert",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "intro",
      "title": "Degrees of Freedom",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Degrees of Freedom"
        },
        {
          "kind": "text",
          "md": "Even simple movement is incredibly complex.\n\nThink about reaching out and pressing one button on a kiosk.\n\nThe task itself sounds simple:\n\n**Move your hand to the button and press it.**\n\nBut your body has many different ways to make that happen.\n\nYour shoulder can move.\n\nYour elbow can move.\n\nYour forearm can rotate.\n\nYour wrist can move.\n\nYour fingers can organize in different ways.\n\nYour trunk can contribute.\n\nYour legs and posture can change how the rest of your body gets there.\n\nAnd there are countless combinations of those movements that could still accomplish the exact same goal.\n\nThat is the basic idea behind **degrees of freedom**."
        },
        {
          "kind": "bigIdea",
          "md": "A degree of freedom is a way that a system is free to change while solving a problem."
        },
        {
          "kind": "text",
          "md": "In movement, the human body has a huge number of available degrees of freedom.\n\nThe coaching question is not simply:\n\n**How do we get more?**\n\nIt is:"
        },
        {
          "kind": "bigIdea",
          "md": "How does the athlete learn to organize all of that freedom into useful movement?"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A Small Scientific Clarification",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "For coaches, it is useful to think of joints and body segments as giving us many movement possibilities.\n\nOne common shortcut is to assume:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "One joint = one degree of freedom.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "That isn't technically accurate.\n\nA joint may allow movement around one, two, three, or more axes depending on how it is modeled.\n\nThere are also degrees of freedom at other levels of the movement system, including muscles and other coordinated elements.\n\nThe important coaching idea is simpler:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "The body has far more possible movement combinations than are strictly necessary to accomplish most tasks.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "For example, positioning and orienting the hand in space requires only a limited number of task-level degrees of freedom, while the arm and body provide many more movement possibilities than are required to reach that position.\n\nThat abundance creates both a coordination challenge and an enormous source of adaptability.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Basketball Makes the Problem Much Bigger"
        },
        {
          "kind": "text",
          "md": "Now move from pressing a button to basketball.\n\nImagine:\n\n- countering quickly off the dribble,\n- changing direction in stride,\n- getting back on balance,\n- or organizing a jump shot at speed.\n\nNow the athlete has to coordinate:\n\n- the feet,\n- ankles,\n- knees,\n- hips,\n- trunk,\n- shoulders,\n- arms,\n- wrists,\n- hands,\n- and the timing between all of them.\n\nAnd all of this may be happening while:\n\n- moving quickly,\n- reacting to a defender,\n- controlling the ball,\n- seeing the floor,\n- and trying to achieve a precise outcome.\n\nThere is no single possible way all of those pieces can combine.\n\nThe body has many possible solutions.\n\nThat is powerful.\n\nBut it is also a lot to control."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Bernstein's Degrees-of-Freedom Problem",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Nikolai Bernstein made this one of the central problems of motor control.\n\nIf the body has many more movement possibilities than are strictly necessary for a task:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "How does the performer organize all of them into one coordinated action?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This became known as the **degrees-of-freedom problem**.\n\nEarly interpretations framed the extra degrees of freedom as a kind of redundancy the nervous system had to control.\n\nMore modern approaches often describe this less as a problem and more as **motor abundance**.\n\nThe extra possibilities are not necessarily useless.\n\nThey can allow the athlete to:\n\n- adapt,\n- compensate,\n- stabilize an outcome,\n- respond to perturbations,\n- and solve secondary problems while preserving the main goal.\n\nSo the goal of skill is not to eliminate freedom.\n\nIt is to organize it.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "stiff",
      "title": "Why Beginners Often Look Stiff",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Why Beginners Often Look Stiff"
        },
        {
          "kind": "text",
          "md": "When a movement is unfamiliar, all of those possible movement combinations can become overwhelming.\n\nOne way the body simplifies the problem is by **freezing degrees of freedom**.\n\nIn practical terms:"
        },
        {
          "kind": "bigIdea",
          "md": "Lock some things down so there are fewer things to control."
        },
        {
          "kind": "text",
          "md": "That is one reason beginners often look stiff.\n\nIt can also happen to an experienced basketball player learning a movement they have never performed before.\n\nThey may be highly skilled overall.\n\nBut in that specific movement, they are a beginner."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Example: A New Crossover"
        },
        {
          "kind": "text",
          "md": "Watch a younger or less experienced player try an unfamiliar crossover.\n\nThe hand crossing the ball may move.\n\nBut the opposite arm might become extremely rigid.\n\nThe trunk may barely rotate.\n\nThe body can look fixed around the one piece the athlete is consciously trying to control.\n\nThat stiffness is not necessarily random.\n\nIt can be the athlete's solution to complexity."
        },
        {
          "kind": "bigIdea",
          "md": "Fewer moving parts = a simpler problem to organize."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Example: Shooting"
        },
        {
          "kind": "text",
          "md": "The same thing can happen in shooting.\n\nA fluid shot requires many parts of the movement to coordinate over time.\n\nBut an athlete who cannot comfortably coordinate all of those pieces may organize the shot into rigid phases.\n\nThe arms move.\n\nThen the lower body moves.\n\nOr the lower body finishes one phase before the upper body can continue.\n\nInstead of the movement flowing through the chain, different pieces appear to turn on and off separately.\n\nThat can contribute to the visual appearance of a hitch.\n\nBut we need to be careful:"
        },
        {
          "kind": "bigIdea",
          "md": "Not every hitch is a degrees-of-freedom problem."
        },
        {
          "kind": "text",
          "md": "BAM commonly sees at least two different possibilities.\n\nOne is a coordination problem where the athlete is locking parts of the movement and struggling to sequence them smoothly.\n\nAnother is a learned force-regulation strategy, where a pause or interruption helps the athlete take speed or power off the ball.\n\nSimilar-looking movements can come from different underlying problems.\n\nSo do not diagnose the cause from appearance alone."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Freezing and Freeing Degrees of Freedom",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Bernstein proposed that early learners often simplify coordination by **freezing** degrees of freedom.\n\nAs skill develops, performers can gradually **free** more of them.\n\nResearch generally provides support for this idea, but not as a universal rule.\n\nA systematic review identified 13 studies examining freezing and freeing during motor learning. Ten of the 13 reported evidence consistent with freezing early in learning.\n\nBut the exact pattern depended on:\n\n- the task,\n- the individual,\n- the phase of movement,\n- and the instructional conditions.\n\nThat matters.\n\nThis does **not** mean every skill develops through the same fixed sequence:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Freeze → free, every time.",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A more accurate interpretation is:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Freezing degrees of freedom is one common way athletes simplify difficult coordination problems.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "freeing",
      "title": "Freeing Degrees of Freedom",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Freeing Degrees of Freedom"
        },
        {
          "kind": "text",
          "md": "As the athlete becomes more comfortable with the skill, they can begin to release some of those rigid solutions.\n\nThe opposite arm can move.\n\nThe trunk can contribute.\n\nThe lower and upper body can coordinate instead of operating in separate chunks.\n\nMore of the chain becomes available.\n\nThis does **not** automatically mean the movement becomes good.\n\nAn athlete can have access to many degrees of freedom and still organize them poorly.\n\nThat is the player who looks:\n\n**loose**\n\n**wild**\n\n**all over the place**\n\nThey have freedom.\n\nThey do not yet have enough control over that freedom.\n\nSo:"
        },
        {
          "kind": "bigIdea",
          "md": "More degrees of freedom is not the same thing as more skill."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Degrees of Freedom and Fluidity"
        },
        {
          "kind": "text",
          "md": "Freeing degrees of freedom is also not synonymous with fluidity.\n\nA player can be fluid within a relatively narrow movement solution.\n\nFor example, a strong shooter may use relatively little contribution from certain parts of the lower body and still produce an extremely smooth shot.\n\nBut for more complex, high-speed, reactive movements that require the entire chain to reorganize:"
        },
        {
          "kind": "bigIdea",
          "md": "Access to enough degrees of freedom becomes a prerequisite for advanced fluidity."
        },
        {
          "kind": "text",
          "md": "You cannot coordinate movement options you do not have access to.\n\nAt the same time:"
        },
        {
          "kind": "bigIdea",
          "md": "Access without coordination can simply look loose and uncontrolled."
        },
        {
          "kind": "text",
          "md": "The goal is not freedom by itself.\n\nThe goal is **organized freedom**."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "BAM Coaching Bias: Err Slightly Toward Freedom"
        },
        {
          "kind": "text",
          "md": "In BAM's experience, an athlete who has access to a lot of movement but lacks control is often easier to develop than an athlete who is extremely rigid and cannot access enough movement options.\n\nA loose athlete can gain control through:\n\n- movement challenges,\n- practice,\n- experience,\n- constraints,\n- and learning when different movement solutions are useful.\n\nUnlocking degrees of freedom in a chronically stiff athlete can be more difficult.\n\nThat is a BAM coaching observation, not a universal scientific law.\n\nBut it influences our practice design:"
        },
        {
          "kind": "bigIdea",
          "md": "We would usually rather teach an athlete to organize available freedom than build an athlete who is afraid to move outside one rigid pattern."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "From Redundancy to Motor Abundance",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Older descriptions often called the body's extra movement possibilities **redundant**.\n\nThat sounds like they are unnecessary.\n\nA more useful modern idea is **motor abundance**.\n\nThe body has more movement options than the task strictly requires.\n\nThat abundance can be beneficial.\n\nDifferent joints or body segments can vary while the important task outcome stays stable.\n\nResearchers sometimes call this **good variance**.\n\nThe movement itself can change without harming the goal.\n\nThat allows the system to:\n\n- absorb perturbations,\n- perform secondary tasks,\n- adapt to changing conditions,\n- and preserve important outcomes.\n\nThis is one reason expert movement does not have to mean reproducing every joint angle identically.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "title": "Synergies: Freedom Without Chaos",
          "blocks": [
            {
              "kind": "prose",
              "md": "If every degree of freedom had to be independently controlled at every moment, movement would become an enormous control problem.\n\nOne proposed solution is **synergy**.\n\nDifferent movement components can coordinate together so that variation in one component is compensated for by another.\n\nThe important outcome stays relatively stable even though the pieces producing it vary.\n\nThis is an important distinction:\n\n> **Coordination does not require eliminating variability.**\n\nIt can mean organizing variability so that it helps preserve the outcome.\n\nThat is another reason the goal is not simply:\n\n**Freeze everything into one perfect technique.**"
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "decisions",
      "title": "Sometimes We Should Close Degrees of Freedom",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Sometimes We Should Close Degrees of Freedom"
        },
        {
          "kind": "text",
          "md": "If freedom can be useful, it is tempting to conclude:"
        },
        {
          "kind": "bigIdea",
          "md": "More freedom is always better."
        },
        {
          "kind": "text",
          "md": "It is not.\n\nImagine opening Google Maps in an area with 50 restaurants.\n\nTechnically, having 50 options gives you more freedom.\n\nBut if you have no idea what you want, the number of choices can make the decision harder.\n\nSo you filter.\n\n**Korean food.**\n\n**Within ten minutes.**\n\n**Open right now.**\n\nYou temporarily remove possibilities so the problem becomes manageable.\n\nWe can do the same thing in training."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Degrees of Freedom in Decision-Making"
        },
        {
          "kind": "text",
          "md": "We can also use the idea as a coaching lens for the athlete's available **action solutions**.\n\nThis is a broader BAM application of the concept, not the literal biomechanical definition of a degree of freedom.\n\nImagine a player driving into the paint.\n\nFunctionally, their only solution might be:\n\n**One-foot layup.**\n\nAnother athlete may have access to several possibilities:\n\n- a floater,\n- stopping on two feet,\n- changing the pickup,\n- or another developed finishing solution.\n\nWhy might the first player have fewer usable options?\n\nSeveral possibilities:\n\n- they cannot physically execute the other actions,\n- they do not perceive those affordances,\n- they do not trust those actions,\n- or they have become heavily attracted to one familiar solution.\n\nThat connects directly to our Affordances module."
        },
        {
          "kind": "bigIdea",
          "md": "An option is not functionally useful just because a coach can imagine it."
        },
        {
          "kind": "text",
          "md": "The athlete has to be able to execute it, perceive when it is available, and trust it enough to use it."
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            }
          ]
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Expand the Toolbox or Improve Selection?"
        },
        {
          "kind": "text",
          "md": "Sometimes the athlete genuinely needs more solutions.\n\nTheir action capacity is too narrow.\n\nIn that case:"
        },
        {
          "kind": "bigIdea",
          "md": "Open more possibilities."
        },
        {
          "kind": "text",
          "md": "Other times, the athlete already has enough useful solutions.\n\nThe issue is that they do not select between them well.\n\nIn that case:"
        },
        {
          "kind": "bigIdea",
          "md": "Adding another move may not solve the problem."
        },
        {
          "kind": "text",
          "md": "The priority may become:\n\n- attunement,\n- recognizing information,\n- selecting the right option,\n- improving execution,\n- or sharpening the solutions they already possess.\n\nThis is a coach's judgment.\n\nA smaller player facing highly variable environments may benefit from a particularly deep toolbox.\n\nAnother athlete may already have plenty of solutions and simply need to use them better."
        },
        {
          "kind": "bigIdea",
          "md": "If they lack useful solutions, expand them. If they have enough solutions but use them poorly, improve selection."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Degrees of Freedom vs. Affordances",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "These concepts overlap, but they are not interchangeable.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Affordances",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "What opportunities for action emerge from the athlete-environment relationship?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Degrees of freedom",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "What possibilities can vary within the system as the athlete organizes an action?\n\nBAM sometimes extends the degrees-of-freedom lens to an athlete's available solution set.\n\nWhen we do that, we are using the idea practically rather than claiming that a floater and a two-foot stop are literal biomechanical degrees of freedom.\n\nThat distinction keeps the science clean while preserving the usefulness of the lens.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "practice",
      "title": "Degrees of Freedom in Practice Design",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Degrees of Freedom in Practice Design"
        },
        {
          "kind": "text",
          "md": "This concept becomes especially useful when designing activities.\n\nEvery drill or small-sided game contains possibilities.\n\nSome are open.\n\nSome are closed.\n\nThe coach can manipulate that intentionally.\n\nImagine a 1v1 or 1v2 activity.\n\nIf the athlete can:\n\n- start the possession many different ways,\n- beat the first defender many different ways,\n- and finish the possession many different ways,\n\nthe total solution space becomes huge.\n\nThat might be exactly what we want.\n\nBut not always."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Close One Area to Open Another"
        },
        {
          "kind": "text",
          "md": "Suppose the main goal is to explore finishing.\n\nWe can reduce variability earlier in the repetition.\n\nUse the same start.\n\nCreate a similar initial advantage.\n\nThen leave the finish open.\n\nNow the athlete has less to solve before reaching the thing we actually want them exploring.\n\nOr we can do the opposite.\n\nAllow athletes freedom in how they create the advantage.\n\nThen constrain the finish to:\n\n**two feet.**\n\nNow we have closed the solution space at the rim so they spend more repetitions exploring a particular family of finishing solutions."
        },
        {
          "kind": "bigIdea",
          "md": "Sometimes we close degrees of freedom in one part of the problem so the athlete can explore more deeply somewhere else."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A Practice-Design Audit"
        },
        {
          "kind": "text",
          "md": "You do not need to see every drill through degrees of freedom.\n\nBut it can be a useful audit.\n\nAsk:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Where does this activity currently give the athlete a lot of freedom?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Is that freedom helping the learning target or creating unnecessary overload?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "What could I temporarily close?"
        },
        {
          "kind": "text",
          "md": "You may close possibilities to:\n\n- reduce cognitive or strategic overload,\n- increase repetition of a particular problem,\n- emphasize a smaller solution range,\n- or make the learning target easier to identify.\n\nThen ask:"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Where should I leave freedom open?"
        },
        {
          "kind": "text",
          "md": "That is where the athlete gets to:\n\n- explore,\n- adapt,\n- solve,\n- or discover different solutions.\n\nThe point is not maximum freedom.\n\nThe point is **purposeful freedom**."
        }
      ]
    },
    {
      "id": "activity1",
      "title": "ACTIVITY 1: Find the Frozen Degrees of Freedom",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 1: Find the Frozen Degrees of Freedom",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "frozen",
          "title": false,
          "numbered": false,
          "intro": "Think of a movement you coach where an athlete often looks stiff.\n\nUse an athlete or movement you actually work with.\n\nAnswer:",
          "steps": [
            {
              "id": "free",
              "q": "1. What parts of the body appear to be moving freely?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "fixed",
              "q": "2. What parts appear unusually fixed?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "simplify",
              "q": "3. Could that stiffness be simplifying the coordination problem?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "unlock",
              "q": "4. What would become possible if one of those currently fixed components were freed?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "help",
              "q": "5. Would freeing it actually help the skill, or are you assuming that more movement is better?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 2: Freedom or Control?",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "athletes",
          "title": false,
          "numbered": false,
          "intro": "Consider two athletes.\n\nFor each athlete answer:\n\n- Do they primarily need more freedom or better organization?\n- What would you emphasize in practice?\n- What would you avoid?\n- How would you know whether the intervention is working?",
          "steps": [
            {
              "id": "a",
              "q": "Athlete A",
              "hint": "Has a large movement solution space but often looks loose and poorly coordinated.",
              "rows": 3
            },
            {
              "id": "b",
              "q": "Athlete B",
              "hint": "Is highly controlled but visibly rigid and has difficulty adapting at speed.",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Then answer:",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "inherit",
          "prompt": "Which athlete would you rather inherit as a coach, and why?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "There is no universal correct answer.\n\nBAM generally skews toward preserving or developing freedom, then helping the athlete organize it.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity3",
      "title": "ACTIVITY 3: Expand or Sharpen the Toolbox?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 3: Expand or Sharpen the Toolbox?",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "toolbox",
          "title": false,
          "numbered": false,
          "intro": "Choose one athlete and one game situation.\n\nList the solutions they can **actually use**, not every option that theoretically exists.\n\nThen ask:",
          "steps": [
            {
              "id": "few",
              "q": "Are there too few functional solutions?",
              "hint": "If yes:\n\nWhat capability would be most useful to add?",
              "rows": 3
            },
            {
              "id": "enough",
              "q": "Or do they already have enough?",
              "hint": "If yes:\n\nIs the bigger problem:\n\n- perception,\n- confidence,\n- selection,\n- execution,\n- or understanding when each solution applies?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Your answer should determine whether practice emphasizes **adding options** or **sharpening the use of existing ones**.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "ACTIVITY 4: Open and Close the Practice",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "parts",
          "title": false,
          "numbered": false,
          "intro": "Choose one activity you already coach.\n\nDo not create a new basketball scenario.\n\nMap the activity into three parts:",
          "steps": [
            {
              "id": "start",
              "q": "Start",
              "hint": "What possibilities are available?",
              "rows": 3
            },
            {
              "id": "middle",
              "q": "Middle",
              "hint": "What possibilities are available?",
              "rows": 3
            },
            {
              "id": "finish",
              "q": "Finish",
              "hint": "What possibilities are available?",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "openclose",
          "title": false,
          "numbered": false,
          "intro": "Now answer:",
          "steps": [
            {
              "id": "close",
              "q": "Where could you close degrees of freedom to reduce overload or increase repetition?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "open",
              "q": "Where should you keep degrees of freedom open so the athlete can explore?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "allopen",
              "q": "What would happen if everything were open?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "allclosed",
              "q": "What would happen if everything were closed?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Then redesign the activity with a clear reason for each choice.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "map",
      "title": "A Simple Map",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "A Simple Map"
        },
        {
          "kind": "framework",
          "steps": [
            {
              "q": "TOO MANY POSSIBILITIES + LOW CONTROL",
              "md": "The athlete may look loose, inconsistent, or overwhelmed.\nNeed: Better organization."
            },
            {
              "q": "TOO FEW ACCESSIBLE POSSIBILITIES",
              "md": "The athlete may look rigid or have a narrow solution set.\nNeed: Potentially free or expand useful degrees of freedom."
            },
            {
              "q": "ENOUGH USEFUL POSSIBILITIES + GOOD COORDINATION",
              "md": "The athlete can organize movement while adapting to the problem.\nNeed: Continue sharpening, selecting, and adapting."
            },
            {
              "q": "PRACTICE HAS TOO MANY OPEN POSSIBILITIES",
              "md": "The learning target may disappear inside the complexity.\nNeed: Close some possibilities intentionally."
            },
            {
              "q": "PRACTICE HAS TOO FEW OPEN POSSIBILITIES",
              "md": "The athlete may only learn to reproduce one answer.\nNeed: Open the relevant solution space."
            }
          ]
        },
        {
          "kind": "reflection",
          "md": "Think of one athlete who looks unusually stiff in a particular movement.\n\nAsk:\n\n**What might they be locking down to make the movement easier to control?**\n\nThen ask the opposite:\n\n**If I freed that part of the movement, would it actually help them solve the basketball problem better?**",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "reflection",
          "title": false,
          "numbered": false,
          "intro": "Choose one skill or activity you coach frequently.\n\nAnswer:",
          "steps": [
            {
              "id": "comfortable",
              "q": "What degrees of freedom does the athlete currently access comfortably?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "rigid",
              "q": "Where do they appear rigid?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "loose",
              "q": "Where do they have freedom but lack control?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "unlock",
              "q": "What movement possibility might be useful to unlock?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "enough",
              "q": "What solution might they already have enough of?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "close",
              "q": "In your practice design, what could you temporarily close?",
              "hint": "",
              "rows": 3
            },
            {
              "id": "open",
              "q": "What should remain open?",
              "hint": "",
              "rows": 3
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Finally:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Am I trying to create more freedom, or better organization of the freedom that already exists?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Those are different coaching problems.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway"
        },
        {
          "kind": "text",
          "md": "The human body has an enormous number of ways to move.\n\nThat is both a challenge and an advantage.\n\nWhen a movement is unfamiliar, athletes may simplify the problem by locking some of those possibilities down.\n\nThat can make them look stiff.\n\nAs skill develops, more of the movement can become available.\n\nBut:"
        },
        {
          "kind": "bigIdea",
          "md": "More freedom does not automatically mean better movement."
        },
        {
          "kind": "text",
          "md": "Freedom without coordination can become chaos.\n\nAnd coordination without enough freedom can become rigidity.\n\nThe goal is not to maximize degrees of freedom.\n\nIt is to help athletes access and organize the degrees of freedom that help them solve the problem.\n\nThe same idea can guide practice design.\n\nSometimes we open possibilities so athletes can explore.\n\nSometimes we close possibilities so they can focus, repeat, or explore a smaller space more deeply."
        },
        {
          "kind": "bigIdea",
          "md": "Freedom is only useful if the athlete can organize it."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Suggested References",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "- Bernstein, N. A. (1967). The Co-ordination and Regulation of Movements. Foundational formulation of the degrees-of-freedom problem and freezing/freeing ideas.\n- Guimarães, A. N. et al. (2020). Systematic review of freezing and freeing degrees of freedom during motor learning; 10 of 13 included studies reported evidence consistent with early freezing.\n- Gray, R. (2020). Changes in Movement Coordination Associated With Skill Acquisition in Baseball Batting: Freezing/Freeing Degrees of Freedom and Functional Variability. Frontiers in Psychology.\n- Latash, M. L. (2012). The Bliss of Motor Abundance. Experimental Brain Research. Reframes motor “redundancy” as abundance and emphasizes useful task-relevant variability.\n- Latash, M. L. and colleagues — work on motor abundance, synergies, uncontrolled-manifold approaches, and coordination of redundant degrees of freedom.",
          "depth": "cert"
        },
        {
          "kind": "podcastLink",
          "title": "Degrees of Freedom",
          "url": "https://podcasts.apple.com/us/podcast/degrees-of-freedom-the-hidden-key-to-better/id1604852096?i=1000765671711"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            },
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "differential-learning-concept",
              "title": "Differential Learning",
              "blurb": "Push the Borders. Reorganize the Skill."
            }
          ]
        }
      ]
    }
  ]
};
